import type {
  CreateYearResponseDto,
  GetYearsResponseDto,
  UpdateYearRequestDto,
  YearDto,
} from 'shared/dtos/years.dto';
import type { AppState } from 'store';

import { createAsyncThunk } from '@reduxjs/toolkit';
import YearsService from 'services/years.service';

const yearsService = new YearsService();

export const getYearsThunk = createAsyncThunk<GetYearsResponseDto>(
  'years/getYears',
  async (_, thunkAPI) => {
    try {
      return await yearsService.getYears();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const createYearThunk = createAsyncThunk<
  CreateYearResponseDto,
  void,
  { state: AppState }
>(
  'years/createYear',
  async (_, thunkAPI) => {
    try {
      return await yearsService.createYear();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (state.years.isCreateYearThunkPending) {
        return false;
      }
      return true;
    },
  },
);

export const updateYearThunk = createAsyncThunk<
  YearDto,
  UpdateYearRequestDto & { id: number },
  { state: AppState }
>(
  'years/updateYear',
  async (payload, thunkAPI) => {
    try {
      const { id, ...data } = payload;
      const state = thunkAPI.getState().years;
      const result = await yearsService.updateYear(id, data);

      if (state.currentYearId === result.id) {
        localStorage.setItem('currentYearFrozen', String(!result.enabled));
        localStorage.setItem(
          'currentYearGoalFrozen',
          String(!result.frozenYearGoals),
        );
      }

      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (state.years.isUpdateYearThunkPending) {
        return false;
      }
      return true;
    },
  },
);

export const removeYearThunk = createAsyncThunk<
  number,
  number,
  { state: AppState }
>(
  'years/removeYear',
  async (id: number, thunkAPI) => {
    try {
      await yearsService.removeYear(id);

      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (state.years.isRemoveYearThunkPending) {
        return false;
      }
      return true;
    },
  },
);
